import React, { useState, useEffect, useCallback, useRef, memo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { api, formatCurrency } from '../../utils/api'
import { applyColourAndRatio, applyColourAndRatioBlob, removeBackground, removeBackgroundBody, urlToBase64, validateImageFile, checkImageQuality, loadImage, readStraightenRegion, straightenToCanvas, padToPortrait, rotateQuarterTurns } from '../../utils/imageProcessing'
import { straightenPreview as straightenPreviewStyle, STRAIGHTEN_MAX_DEGREES, STRAIGHTEN_STEP_DEGREES } from '../../utils/imageStraighten'
import { uploadProcessedPhoto } from '../../utils/photoUpload'
import { useAuth } from '../../hooks/useAuth'
import { photographyConditionLabel } from './photographyConditionLabel'
import { photographyAccessoriesLabel } from './photographyAccessories'
import { photoThumbnailUrl } from './photoThumbnail'
import { filterPhotographyQueue } from './photographyQueueSearch'
import { itemQuantity } from '../../../../shared/item-count.js'
import { runPool } from '../../../../shared/run-pool.js'
import QuoteNotesLayout from './QuoteNotes'
import DealLink from '../shared/DealLink.jsx'
import QuantityBadge from '../shared/QuantityBadge.jsx'
import { QueuedReturn } from './ManualListingTrade'
import RemoveFromQueueDialog from './RemoveFromQueueDialog.jsx'

// Photos processed at once by Process All. Each one holds full-size canvases
// (~150-200 MB), so this is capped for the warehouse PC, not the network.
const PROCESS_CONCURRENCY = 3

// How long the "removed from the queue" confirmation stays up.
const TOAST_MS = 4000

// Design System (matching warehouse components)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    blueLight: '#EDF3F6'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Source labels and colours
const SOURCE_LABELS = {
  testing: 'Testing',
  issue_trades: 'Issue Trades',
  repairs: 'Repairs',
  manual_listing: 'Manual Listing'
}
const SOURCE_COLORS = {
  testing: '#FFD44D',
  issue_trades: '#E5625E',
  repairs: '#8B5CF6',
  manual_listing: '#4E8098'
}

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completing: 'Creating draft',
  completed: 'Completed'
}
const STATUS_BADGE_COLORS = {
  pending: 'amber',
  in_progress: 'blue',
  completing: 'blue',
  completed: 'green'
}

// Reusable UI primitives (matching warehouse pattern)
const Card = ({ children, style = {}, padding = true }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick, style = {} }) => {
  const variants = {
    primary: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    secondary: { background: theme.colors.white, color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` },
    success: { background: theme.colors.greenOnions, color: theme.colors.white },
    danger: { background: theme.colors.crimson, color: theme.colors.white },
    ghost: { background: 'transparent', color: theme.colors.grey500 }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s, ...style,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20 }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${theme.colors.grey200}`,
    borderTopColor: theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

// --- Utility helpers ---

function formatTimeAgo(dateString) {
  if (!dateString) return '-'
  const now = new Date()
  const date = new Date(dateString)
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]
      resolve({ base64, contentType: file.type })
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

// --- Sub-components ---

function SourceBadge({ source }) {
  const label = SOURCE_LABELS[source] || source
  const color = SOURCE_COLORS[source] || theme.colors.grey400
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: `${color}20`,
      color: color
    }}>
      {label}
    </span>
  )
}

function ProgressBar({ current, total, label }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0
  return (
    <div style={{ width: '100%' }}>
      {label && (
        <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '6px' }}>
          {label} ({current}/{total})
        </div>
      )}
      <div style={{
        width: '100%',
        height: '8px',
        background: theme.colors.grey200,
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: theme.colors.greenOnions,
          borderRadius: '4px',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  )
}

function PhotoEditModal({ photo, itemId, onClose, onProcessed, onBgRemovalUsed }) {
  const baseUrl = photo?.url
  const [imageAdjustments, setImageAdjustments] = useState({ brightness: 100, contrast: 100, autoApplied: false })
  const [straightenDegrees, setStraightenDegrees] = useState(0)
  const [showStraightenGrid, setShowStraightenGrid] = useState(false)
  const [previewFrame, setPreviewFrame] = useState(null)
  const [cropMode, setCropMode] = useState(false)
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 100, height: 100 })
  const [processedImage, setProcessedImage] = useState(photo?.processedUrl || null)
  const [removingBg, setRemovingBg] = useState(false)
  const [makingRatio, setMakingRatio] = useState(false)
  const [rotating, setRotating] = useState(false)
  const [autoProcessing, setAutoProcessing] = useState(false)
  const [autoProcessStep, setAutoProcessStep] = useState('')
  const [bgSource, setBgSource] = useState(null)
  const [bgCredits, setBgCredits] = useState(null)
  const [saving, setSaving] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [modalToast, setModalToast] = useState(null)
  const bgCreditUsedRef = useRef(false)

  const bgAlreadyUsed = !!photo?.bg_removal_used

  function showModalToast(message, type = 'error') {
    setModalToast({ message, type })
    setTimeout(() => setModalToast(null), 5000)
  }

  const imageRef = useRef(null)
  const cropStartRef = useRef(null)

  useEffect(() => {
    if (!photo) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { handleClose() }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [photo])

  // The preview needs the image's shape and straighten region to match what Save draws.
  const previewSrc = processedImage || baseUrl
  useEffect(() => {
    setPreviewFrame(null)
    if (!previewSrc) return
    let alive = true
    loadImage(previewSrc)
      .then(image => {
        if (alive) setPreviewFrame({ width: image.width, height: image.height, region: readStraightenRegion(image) })
      })
      .catch(err => console.error('Failed to read image for straightening:', err))
    return () => { alive = false }
  }, [previewSrc])

  if (!photo) return null

  // --- Helpers ---

  function resetImageAdjustments() {
    setImageAdjustments({ brightness: 100, contrast: 100, autoApplied: false })
    setStraightenDegrees(0)
    setCropMode(false)
    setProcessedImage(null)
    setBgSource(null)
    setBgCredits(null)
  }

  function handleClose() {
    if (bgCreditUsedRef.current) {
      showModalToast('You have to save the image as you have used a Remove BG API credit.')
      return
    }
    resetImageAdjustments()
    onClose()
  }

  function getImageFilter() {
    return `brightness(${imageAdjustments.brightness}%) contrast(${imageAdjustments.contrast}%)`
  }

  /** The current image with the straighten angle and colour filter baked in. */
  async function renderStraightened(src = processedImage || baseUrl, filter = getImageFilter()) {
    const image = await loadImage(src)
    return straightenToCanvas(image, { degrees: straightenDegrees, filter })
  }

  function cropCanvas(source) {
    const scaleX = source.width / 100
    const scaleY = source.height / 100
    const canvas = document.createElement('canvas')
    canvas.width = cropArea.width * scaleX
    canvas.height = cropArea.height * scaleY
    canvas.getContext('2d').drawImage(source, cropArea.x * scaleX, cropArea.y * scaleY, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height)
    return canvas
  }

  async function renderToCanvas() {
    const straightened = await renderStraightened()
    return cropMode && cropArea.width < 100 ? cropCanvas(straightened) : straightened
  }

  async function getEditedImageDataUrl() {
    const canvas = await renderToCanvas()
    return canvas.toDataURL('image/jpeg', 0.9)
  }

  function getEditedImageBlob() {
    const MAX_DIM = 2048
    return renderToCanvas().then(canvas => {
      // Downscale for upload — Shopify doesn't need more than 2048px
      if (canvas.width > MAX_DIM || canvas.height > MAX_DIM) {
        const scale = MAX_DIM / Math.max(canvas.width, canvas.height)
        const small = document.createElement('canvas')
        small.width = Math.round(canvas.width * scale)
        small.height = Math.round(canvas.height * scale)
        const ctx = small.getContext('2d')
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, small.width, small.height)
        ctx.drawImage(canvas, 0, 0, small.width, small.height)
        canvas = small
      }
      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', 0.9)
      })
    })
  }

  // --- Handlers ---

  function handleAutoLevel() {
    setImageAdjustments({ brightness: 105, contrast: 110, autoApplied: true })
  }

  function handleBrightnessChange(value) {
    setImageAdjustments(prev => ({ ...prev, brightness: value, autoApplied: false }))
  }

  function handleContrastChange(value) {
    setImageAdjustments(prev => ({ ...prev, contrast: value, autoApplied: false }))
  }

  function handleDiscardProcessedImage() {
    setProcessedImage(null)
    setBgSource(null)
  }

  async function handleRemoveBg() {
    if (bgAlreadyUsed) {
      showModalToast('Due to API costs, you should not re-run background removal. If absolutely necessary, please reupload and try again.')
      return
    }

    setRemovingBg(true)
    try {
      const response = await api.post('/api/image/remove-background', await removeBackgroundBody(`${baseUrl}?t=${Date.now()}`))
      if (response.success && response.image_data) {
        setProcessedImage(`data:${response.content_type};base64,${response.image_data}`)
        setBgSource(response.source || null)
        bgCreditUsedRef.current = true
        if (response.credits_remaining !== undefined) setBgCredits(response.credits_remaining)

        // Log usage in database (update local state even if logging fails)
        if (onBgRemovalUsed) onBgRemovalUsed(photo.photography_photo_id)
        try {
          await api.post(`/api/warehouse/photography/${itemId}/photos/${photo.photography_photo_id}/bg-removal-used`, {
            source: response.source || 'unknown'
          })
        } catch (logErr) {
          console.error('Failed to log bg removal usage:', logErr)
        }
      } else {
        throw new Error('No image data returned')
      }
    } catch (err) {
      console.error('Background removal failed:', err)
      showModalToast('Background removal failed. Please try again.')
    } finally {
      setRemovingBg(false)
    }
  }

  async function handleMake4x5Ratio() {
    setMakingRatio(true)
    try {
      const source = await renderStraightened()
      const padded = padToPortrait(source)
      if (padded.width === source.width && padded.height === source.height) return
      setProcessedImage(padded.toDataURL('image/jpeg', 0.9))
      setStraightenDegrees(0)
    } catch (err) {
      console.error('4:5 ratio conversion failed:', err)
    } finally {
      setMakingRatio(false)
    }
  }

  /**
   * Turn the working image a quarter turn. Only the pixels turn: the colour filter and
   * straighten angle stay live, so they are applied once, at Save. A cutout stays a PNG
   * to keep its transparency; anything else stays a JPEG to keep the image small.
   */
  async function handleRotate(turns) {
    setRotating(true)
    try {
      const source = processedImage || baseUrl
      const rotated = rotateQuarterTurns(await loadImage(source), turns)
      const keepTransparency = source.startsWith('data:image/png')
      setProcessedImage(keepTransparency ? rotated.toDataURL('image/png') : rotated.toDataURL('image/jpeg', 0.95))
    } catch (err) {
      console.error('Rotate failed:', err)
      showModalToast('Rotate failed. Please try again.')
    } finally {
      setRotating(false)
    }
  }

  async function handleAutoProcessAll() {
    const isLead = photo.isLead

    if (isLead && bgAlreadyUsed) {
      showModalToast('Due to API costs, you should not re-run background removal. If absolutely necessary, please reupload and try again.')
      return
    }

    setAutoProcessing(true)
    try {
      setAutoProcessStep('Applying colour correction...')
      const adjustments = { brightness: 105, contrast: 110, autoApplied: true }
      setImageAdjustments(adjustments)
      const filterStr = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%)`

      let imageSrc = baseUrl

      // Only remove background for lead image
      if (isLead) {
        setAutoProcessStep('Removing background...')
        const response = await api.post('/api/image/remove-background', await removeBackgroundBody(`${baseUrl}?t=${Date.now()}`))
        if (!response.success || !response.image_data) throw new Error('Background removal failed')
        imageSrc = `data:${response.content_type};base64,${response.image_data}`
        setBgSource(response.source || null)
        bgCreditUsedRef.current = true
        if (response.credits_remaining !== undefined) setBgCredits(response.credits_remaining)

        // Log usage in database (update local state even if logging fails)
        if (onBgRemovalUsed) onBgRemovalUsed(photo.photography_photo_id)
        try {
          await api.post(`/api/warehouse/photography/${itemId}/photos/${photo.photography_photo_id}/bg-removal-used`, {
            source: response.source || 'unknown'
          })
        } catch (logErr) {
          console.error('Failed to log bg removal usage:', logErr)
        }
      }

      setAutoProcessStep('Applying 4:5 portrait ratio...')
      const source = await renderStraightened(imageSrc, filterStr)
      const finalImage = padToPortrait(source).toDataURL('image/jpeg', 0.9)

      setStraightenDegrees(0)
      setProcessedImage(finalImage)
    } catch (err) {
      console.error('Auto process failed:', err)
      showModalToast('Auto processing failed. Please try individual steps.')
      resetImageAdjustments()
    } finally {
      setAutoProcessing(false)
      setAutoProcessStep('')
    }
  }

  async function handleToggleCrop() {
    if (cropMode) {
      if (cropArea.width < 100 || cropArea.height < 100) {
        try {
          const cropped = cropCanvas(await renderStraightened())
          setProcessedImage(cropped.toDataURL('image/jpeg', 0.9))
          setStraightenDegrees(0)
        } catch (err) {
          console.error('Crop failed:', err)
        }
      }
      setCropMode(false)
      setCropArea({ x: 0, y: 0, width: 100, height: 100 })
    } else {
      setCropMode(true)
      setCropArea({ x: 10, y: 10, width: 80, height: 80 })
    }
  }

  function handleCropMouseDown(e) {
    if (!cropMode) return
    const rect = e.currentTarget.getBoundingClientRect()
    cropStartRef.current = {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100
    }
  }

  function handleCropMouseMove(e) {
    if (!cropMode || !cropStartRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    const startX = Math.min(cropStartRef.current.x, x)
    const startY = Math.min(cropStartRef.current.y, y)
    setCropArea({
      x: Math.max(0, startX),
      y: Math.max(0, startY),
      width: Math.min(100 - startX, Math.abs(x - cropStartRef.current.x)),
      height: Math.min(100 - startY, Math.abs(y - cropStartRef.current.y))
    })
  }

  function handleCropMouseUp() {
    cropStartRef.current = null
  }

  async function handleSave() {
    setSaving(true)
    setUploadProgress(0)
    try {
      const blob = await getEditedImageBlob()
      const formData = new FormData()
      formData.append('photoId', photo.photography_photo_id)
      formData.append('photo', blob, `processed.${blob.type === 'image/png' ? 'png' : 'jpg'}`)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', `/api/warehouse/photography/${itemId}/photos/process`)
        xhr.withCredentials = true
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100))
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(`Save failed: ${xhr.status}`))
        }
        xhr.onerror = () => reject(new Error('Network error during upload'))
        xhr.send(formData)
      })
      bgCreditUsedRef.current = false
      onProcessed()
      handleClose()
    } catch (err) {
      console.error('Save failed:', err)
      showModalToast('Save failed. Please try again.')
    } finally {
      setSaving(false)
      setUploadProgress(0)
    }
  }

  async function handleDownloadImage() {
    try {
      const dataUrl = await getEditedImageDataUrl()
      const link = document.createElement('a')
      link.download = `${photo.filename || 'photo'}-edited.jpg`
      link.href = dataUrl
      link.click()
    } catch (err) {
      console.error('Failed to download image:', err)
    }
  }

  const anyProcessing = autoProcessing || removingBg || makingRatio || rotating
  const straightenPreview = straightenPreviewStyle({ degrees: straightenDegrees, frame: previewFrame })
  const wasReset = photo?.processedUrl && !processedImage
  const hasEdits = processedImage || imageAdjustments.brightness !== 100 || imageAdjustments.contrast !== 100 || straightenDegrees !== 0 || wasReset

  return (
    <div className="lightbox-overlay" onClick={handleClose}>
      <div className="lightbox-content lightbox-with-controls" onClick={e => e.stopPropagation()}>
        <button className="lightbox-close" onClick={handleClose}>&times;</button>

        <div
          className={`lightbox-image-container${straightenDegrees !== 0 ? ' is-straightened' : ''}`}
          onMouseDown={handleCropMouseDown}
          onMouseMove={handleCropMouseMove}
          onMouseUp={handleCropMouseUp}
          onMouseLeave={handleCropMouseUp}
        >
          <img
            ref={imageRef}
            src={processedImage || baseUrl}
            alt={photo.filename}
            className="lightbox-image"
            style={{ filter: getImageFilter(), transform: straightenPreview.transform, transformOrigin: straightenPreview.transformOrigin, background: processedImage ? '#f0f0f0' : 'transparent' }}
            draggable={false}
          />
          {straightenPreview.mask && <div className="straighten-mask" style={straightenPreview.mask} />}
          {showStraightenGrid && <div className="straighten-grid" />}
          {cropMode && (
            <div className="crop-overlay">
              <div
                className="crop-selection"
                style={{
                  left: `${cropArea.x}%`,
                  top: `${cropArea.y}%`,
                  width: `${cropArea.width}%`,
                  height: `${cropArea.height}%`
                }}
              />
            </div>
          )}
        </div>

        {/* Controls — matches inventory lightbox exactly */}
        <div className="lightbox-controls">
          {modalToast && (
            <div style={{
              padding: '8px 14px',
              marginBottom: '8px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              background: modalToast.type === 'error' ? '#fceeed' : '#e8f5ee',
              color: modalToast.type === 'error' ? '#E5625E' : '#3BB273',
              border: `1px solid ${modalToast.type === 'error' ? '#E5625E' : '#3BB273'}40`
            }}>
              {modalToast.message}
            </div>
          )}
          <div className="lightbox-controls-row">
            <button
              className="btn btn-sm btn-primary"
              onClick={handleAutoProcessAll}
              disabled={anyProcessing}
              style={photo.isLead && bgAlreadyUsed ? { opacity: 0.5 } : undefined}
              title={photo.isLead ? (bgAlreadyUsed ? 'BG removal already used on this photo' : 'Auto Level + Remove BG + 4:5 Portrait') : 'Auto Level + 4:5 Portrait'}
            >
              {autoProcessing ? autoProcessStep || 'Processing...' : photo.isLead && bgAlreadyUsed ? 'Auto Process (BG used)' : photo.isLead ? 'Auto Process All' : 'Auto Process'}
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleAutoLevel} disabled={anyProcessing}>
              Auto Level
            </button>
            <button
              className={`btn btn-sm ${cropMode ? 'btn-accent' : 'btn-secondary'}`}
              onClick={handleToggleCrop}
              disabled={anyProcessing}
            >
              {cropMode ? 'Apply Crop' : 'Crop'}
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => handleRotate(-1)} disabled={anyProcessing || cropMode} title="Rotate 90° anticlockwise">
              Rotate Left
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => handleRotate(1)} disabled={anyProcessing || cropMode} title="Rotate 90° clockwise">
              Rotate Right
            </button>
            {photo.isLead && (
              <button
                className={`btn btn-sm ${processedImage && bgAlreadyUsed ? 'btn-accent' : 'btn-secondary'}`}
                onClick={processedImage && bgAlreadyUsed ? handleDiscardProcessedImage : handleRemoveBg}
                disabled={anyProcessing || (bgAlreadyUsed && !processedImage)}
                style={bgAlreadyUsed && !processedImage ? { opacity: 0.5 } : undefined}
                title={bgAlreadyUsed && !processedImage ? 'BG removal already used — reupload to try again' : undefined}
              >
                {removingBg ? 'Processing...' : processedImage && bgAlreadyUsed ? 'Undo BG' : bgAlreadyUsed ? 'BG Used ✓' : 'Remove BG'}
              </button>
            )}
            <button
              className="btn btn-sm btn-secondary"
              onClick={handleMake4x5Ratio}
              disabled={anyProcessing}
              title="Add whitespace for 4:5 portrait ratio"
            >
              {makingRatio ? 'Processing...' : '4:5 Portrait'}
            </button>
            <button className="btn btn-sm btn-secondary" onClick={resetImageAdjustments} disabled={anyProcessing}>
              Reset
            </button>
          </div>
          <div className="lightbox-controls-row">
            <label className="lightbox-slider-label">
              Brightness: {imageAdjustments.brightness}%
              <input
                type="range" min="50" max="150"
                value={imageAdjustments.brightness}
                onChange={e => handleBrightnessChange(parseInt(e.target.value))}
                className="lightbox-slider"
              />
            </label>
            <label className="lightbox-slider-label">
              Contrast: {imageAdjustments.contrast}%
              <input
                type="range" min="50" max="150"
                value={imageAdjustments.contrast}
                onChange={e => handleContrastChange(parseInt(e.target.value))}
                className="lightbox-slider"
              />
            </label>
            <label className="lightbox-slider-label" title="Double-click to set back to level">
              Straighten: {straightenDegrees.toFixed(1)}°
              <input
                type="range" min={-STRAIGHTEN_MAX_DEGREES} max={STRAIGHTEN_MAX_DEGREES} step={STRAIGHTEN_STEP_DEGREES}
                value={straightenDegrees}
                onChange={e => setStraightenDegrees(parseFloat(e.target.value))}
                onDoubleClick={() => setStraightenDegrees(0)}
                onPointerDown={() => setShowStraightenGrid(true)}
                onPointerUp={() => setShowStraightenGrid(false)}
                onKeyDown={() => setShowStraightenGrid(true)}
                onKeyUp={() => setShowStraightenGrid(false)}
                onBlur={() => setShowStraightenGrid(false)}
                disabled={anyProcessing}
                className="lightbox-slider"
              />
            </label>
          </div>
          <div className="lightbox-controls-row">
            <button className="btn btn-sm btn-secondary" onClick={handleDownloadImage}>
              Download
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={saving || !hasEdits || anyProcessing}
              style={(saving || !hasEdits || anyProcessing) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              {saving ? `Uploading${uploadProgress > 0 ? ` ${uploadProgress}%` : '...'}` : anyProcessing ? 'Please wait...' : 'Save'}
            </button>
          </div>
          {cropMode && (
            <div className="lightbox-crop-hint">Drag on image to select crop area</div>
          )}
          {processedImage && (
            <div className="lightbox-crop-hint" style={{ color: '#4ade80' }}>
              Image modified{bgSource ? ` (via ${bgSource})` : ''}{bgCredits !== null ? ` — ${bgCredits} credits left` : ''} — Click "Save" to keep changes
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


// =============================================
// QueueView — lists pending photography items
// =============================================

function QueueView({ items, loading, loadFailed, onStartItem, onRemoveItem, onRefresh, isAdmin }) {
  const [addingDummy, setAddingDummy] = useState(false)
  const [query, setQuery] = useState('')
  const shownItems = filterPhotographyQueue(items, query)
  const filtering = shownItems !== items

  async function handleAddDummy() {
    setAddingDummy(true)
    try {
      await api.post('/api/warehouse/photography/queue', {
        source: 'testing',
        productName: 'TEST \u2014 Dummy Photography Item',
        productType: 'Testing Unit',
        brand: 'Test Brand',
        sellPrice: 99.99,
        buyPrice: 49.99,
        quantity: 1,
        overallCondition: 'Excellent'
      })
      onRefresh()
    } catch (error) {
      console.error('Failed to add dummy item:', error)
    } finally {
      setAddingDummy(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size={28} />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: theme.colors.charcoal }}>
            Photography Queue
          </h2>
          <Badge color="blue">{filtering ? `${shownItems.length} of ${items.length}` : items.length} items</Badge>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isAdmin && (
            <Button variant="secondary" size="sm" onClick={handleAddDummy} disabled={addingDummy}>
              {addingDummy ? 'Adding...' : '+ Dummy Item'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onRefresh}>Refresh</Button>
        </div>
      </div>

      {loadFailed && (
        <Card>
          <div role="alert" style={{ textAlign: 'center', padding: '48px 0', color: theme.colors.grey500 }}>
            <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Could not load the photography queue</div>
            <div style={{ fontSize: '14px', marginBottom: '16px' }}>The items are still there. Try again in a moment.</div>
            <Button size="sm" onClick={onRefresh}>Try again</Button>
          </div>
        </Card>
      )}

      {/* Empty state */}
      {!loadFailed && items.length === 0 && (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px 0', color: theme.colors.grey500 }}>
            <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>No Items for Photography</div>
            <div style={{ fontSize: '14px' }}>All items have been photographed.</div>
          </div>
        </Card>
      )}

      {/* Live search: filters as you type, Escape clears */}
      {items.length > 0 && (
        <input
          type="search"
          className="form-input"
          placeholder="Search by item, brand, type or trade ID"
          aria-label="Search the photography queue"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setQuery('') }}
          style={{ width: '100%', minHeight: 44, fontSize: 16 }}
          autoFocus
        />
      )}

      {/* Queue table */}
      {items.length > 0 && (
        <Card padding={false}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.colors.grey200}` }}>
                  {['Trade ID', 'Item', 'Source', 'Brand', 'Product Type', 'Queued', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '12px 16px', textAlign: 'left',
                      fontSize: '12px', fontWeight: 600, textTransform: 'uppercase',
                      color: theme.colors.grey500, letterSpacing: '0.5px'
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shownItems.map(item => (
                  <QueueRow key={item.photography_queue_id} item={item} onStart={onStartItem} onRemove={onRemoveItem} />
                ))}
                {shownItems.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: '24px 16px', textAlign: 'center', color: theme.colors.grey500 }}>
                      No items match "{query.trim()}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

// The item name with its x N badge inline after it. The badge and the last word
// wrap together, so the badge never sits alone on a line.
function NameWithQuantity({ name, quantity }) {
  if (itemQuantity({ quantity }) <= 1) return name
  const text = name || ''
  const cut = text.lastIndexOf(' ') + 1
  return (
    <>
      {text.slice(0, cut)}
      <span style={{ whiteSpace: 'nowrap' }}>
        {text.slice(cut)}
        <span style={{ marginLeft: 8 }}><QuantityBadge quantity={quantity} /></span>
      </span>
    </>
  )
}

function QueueRow({ item, onStart, onRemove }) {
  const statusColor = STATUS_BADGE_COLORS[item.status] || 'grey'
  const statusLabel = STATUS_LABELS[item.status] || item.status
  const isInProgress = item.status === 'in_progress'

  return (
    <tr style={{ borderBottom: `1px solid ${theme.colors.grey100}` }}>
      <td style={{ padding: '12px 16px', color: theme.colors.grey500 }}>
        {/* The reference is the id an operator can match to a label or an email;
            source_deal_id is a quote UUID post-cutover. Falls back to it so an
            unresolved row is still searchable. */}
        <PhotographyDealLink item={item} />
      </td>
      <td style={{ padding: '12px 16px', fontWeight: 500, color: theme.colors.charcoal }}>
        <NameWithQuantity name={item.product_name} quantity={item.quantity} />
        {/* Ownership chip (spec §5.5): the queue row carries is_listing_service
            since the unified-photography flow — the shooter should know the
            draft they're about to create is a customer's item. */}
        {item.is_listing_service === 1 && (
          <span style={{ marginLeft: 8 }}><Badge color="purple">Consignment</Badge></span>
        )}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <SourceBadge source={item.source} />
      </td>
      <td style={{ padding: '12px 16px', color: theme.colors.grey500 }}>
        {item.brand || '-'}
      </td>
      <td style={{ padding: '12px 16px', color: theme.colors.grey500 }}>
        {item.product_type || '-'}
      </td>
      <td style={{ padding: '12px 16px', color: theme.colors.grey500 }}>
        {formatTimeAgo(item.created_at)}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <Badge color={statusColor}>{statusLabel}</Badge>
      </td>
      <td style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button size="sm" variant={isInProgress ? 'primary' : 'success'} onClick={() => onStart(item)}>
            {isInProgress ? 'Continue' : 'Start'}
          </Button>
          <Button size="sm" variant="secondary" style={{ color: theme.colors.crimson }} onClick={() => onRemove(item)}>
            Remove from queue
          </Button>
        </div>
      </td>
    </tr>
  )
}


// "1 listing · 5 units": the photographer shoots one listing for several units.
function UnitsNote({ quantity }) {
  const units = itemQuantity({ quantity })
  if (units <= 1) return null
  return (
    <span style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.grey500 }}>
      1 listing · {units} units
    </span>
  )
}

// =============================================
// PhotoEditor — upload, sort, process, complete
// =============================================

export function PhotoEditor({ item, onBack, onComplete }) {
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, pct: 0 })
  const [toast, setToast] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [processProgress, setProcessProgress] = useState({ current: 0, total: 0 })
  const [completing, setCompleting] = useState(false)
  const [previewPhoto, setPreviewPhoto] = useState(null)
  const [qrSession, setQrSession] = useState(null)
  const [qrLoading, setQrLoading] = useState(false)
  const pollRef = useRef(null)
  const lastPhotoIdRef = useRef(0)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const [draggingIndex, setDraggingIndex] = useState(null)
  const [isDragOverZone, setIsDragOverZone] = useState(false)
  const fileInputRef = useRef(null)

  const itemId = item.photography_queue_id
  // A queued return (Trac #1333) can change its sale until the shoot completes.
  const [returnProductId, setReturnProductId] = useState(item.source_return_product_id ?? null)

  useEffect(() => {
    loadPhotos()
    markInProgress()
  }, [itemId])

  async function loadPhotos() {
    setLoading(true)
    try {
      const response = await api.get(`/api/warehouse/photography/${itemId}`)
      setPhotos(response.data?.photos || response.photos || [])
    } catch (error) {
      console.error('Failed to load photos:', error)
    } finally {
      setLoading(false)
    }
  }

  async function markInProgress() {
    try {
      await api.patch(`/api/warehouse/photography/${itemId}`, { status: 'in_progress' })
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  // --- QR Code Upload ---
  async function createQrSession() {
    setQrLoading(true)
    try {
      const response = await api.post(`/api/warehouse/photography/${itemId}/qr-session`)
      setQrSession(response)
      // Track highest photo ID for polling
      lastPhotoIdRef.current = photos.length > 0
        ? Math.max(...photos.map(p => p.photography_photo_id || 0))
        : 0
      startPolling()
    } catch (error) {
      console.error('Failed to create QR session:', error)
    } finally {
      setQrLoading(false)
    }
  }

  function startPolling() {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const response = await api.get(`/api/warehouse/photography/${itemId}/photos/poll?since=${lastPhotoIdRef.current}`)
        if (response.photos && response.photos.length > 0) {
          lastPhotoIdRef.current = Math.max(...response.photos.map(p => p.photography_photo_id))
          await loadPhotos()
        }
      } catch {
        // Silent fail on poll
      }
    }, 3000)
  }

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [])

  const qrUrl = qrSession ? `${window.location.origin}/mobile-photo-upload/${qrSession.sessionToken}` : null

  // --- Upload ---

  function showToast(message, type = 'error') {
    setToast({ message, type })
    const lineCount = message.split('\n').length
    setTimeout(() => setToast(null), Math.max(3000, lineCount * 2000))
  }

  async function handleFiles(files) {
    if (!files || files.length === 0) return

    const existingNames = new Set(photos.map(p => p.filename))
    const duplicates = files.filter(f => existingNames.has(f.name))
    const newFiles = files.filter(f => !existingNames.has(f.name))

    if (duplicates.length > 0) {
      const names = duplicates.map(f => f.name).join(', ')
      showToast(`Duplicate photo${duplicates.length > 1 ? 's' : ''} skipped: ${names}`)
    }

    if (newFiles.length === 0) return

    // Validate all files before uploading any
    const validFiles = []
    const rejections = []
    for (const file of newFiles) {
      const formatError = await validateImageFile(file)
      if (formatError) {
        rejections.push(formatError)
        continue
      }
      const qualityError = await checkImageQuality(file)
      if (qualityError) {
        rejections.push(qualityError)
        continue
      }
      validFiles.push(file)
    }

    if (rejections.length > 0) {
      showToast(rejections.join('\n'))
    }

    if (validFiles.length === 0) return

    setUploading(true)
    setUploadProgress({ current: 0, total: validFiles.length, pct: 0 })
    try {
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i]
        const { base64, contentType } = await fileToBase64(file)
        const body = JSON.stringify({ imageData: base64, filename: file.name, contentType })

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('POST', `/api/warehouse/photography/${itemId}/photos`)
          xhr.setRequestHeader('Content-Type', 'application/json')
          xhr.withCredentials = true
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const filePct = e.loaded / e.total
              const overallPct = Math.round(((i + filePct) / validFiles.length) * 100)
              setUploadProgress({ current: i, total: validFiles.length, pct: overallPct })
            }
          }
          xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`))
          xhr.onerror = () => reject(new Error('Upload failed'))
          xhr.send(body)
        })
      }
      setUploadProgress({ current: validFiles.length, total: validFiles.length, pct: 100 })
      await loadPhotos()
    } catch (error) {
      console.error('Failed to upload photos:', error)
    } finally {
      setUploading(false)
      setUploadProgress({ current: 0, total: 0, pct: 0 })
    }
  }

  function handleFileInput(e) {
    handleFiles(Array.from(e.target.files))
    e.target.value = ''
  }

  function handleDropOnZone(e) {
    e.preventDefault()
    setIsDragOverZone(false)
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'))
    handleFiles(files)
  }

  function handleDragOverZone(e) {
    e.preventDefault()
    setIsDragOverZone(true)
  }

  function handleDragLeaveZone() {
    setIsDragOverZone(false)
  }

  // --- Delete ---

  async function handleDeletePhoto(photoId) {
    try {
      await api.delete(`/api/warehouse/photography/${itemId}/photos/${photoId}`)
      await loadPhotos()
    } catch (error) {
      console.error('Failed to delete photo:', error)
    }
  }

  // --- Reorder (drag & drop within grid) ---

  function handleDragStart(e, index) {
    setDraggingIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', '')
  }

  function handleDragEnd() {
    setDraggingIndex(null)
    setDragOverIndex(null)
  }

  function handleDragOver(e, index) {
    e.preventDefault()
    setDragOverIndex(index)
  }

  async function handleDropOnThumb(e, dropIndex) {
    e.preventDefault()
    const startIndex = draggingIndex
    setDragOverIndex(null)
    setDraggingIndex(null)
    if (startIndex === null || startIndex === dropIndex) return

    const reordered = [...photos]
    const [moved] = reordered.splice(startIndex, 1)
    reordered.splice(dropIndex, 0, moved)
    setPhotos(reordered)

    try {
      const photoIds = reordered.map(p => p.photography_photo_id)
      await api.patch(`/api/warehouse/photography/${itemId}/photos/reorder`, { photoIds })
    } catch (error) {
      console.error('Failed to reorder photos:', error)
      await loadPhotos()
    }
  }

  // --- Process all ---

  async function handleProcessAll() {
    const unprocessed = photos.filter(p => !p.is_processed)
    if (unprocessed.length === 0) {
      showToast('All photos are already processed')
      return
    }

    setProcessing(true)
    setProcessProgress({ current: 0, total: unprocessed.length })

    // The lead is decided now, by position. The grid is locked while this runs,
    // so a reorder cannot move a different photo into the lead slot mid-batch.
    const leadId = photos[0].photography_photo_id
    let processed = 0
    let failedCount = 0
    try {
      // One photo failing must not abandon the rest of the batch: runPool
      // settles every photo, and the operator retries just the stragglers.
      const results = await runPool(unprocessed, PROCESS_CONCURRENCY, async (photo) => {
        await processPhoto(photo, photo.photography_photo_id === leadId)
        processed++
        setProcessProgress({ current: processed, total: unprocessed.length })
      })
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Failed to process photo ${unprocessed[index].photography_photo_id}:`, result.reason)
        }
      })
      failedCount = results.filter(r => r.status === 'rejected').length
    } finally {
      // Reflect whatever DID save, even on partial failure, so a retry picks up
      // only the stragglers instead of re-uploading the whole set.
      try { await loadPhotos() } catch (e) { console.error('Failed to refresh photos:', e) }
      setProcessing(false)
    }

    if (failedCount === 0) {
      showToast(`Processed ${unprocessed.length} photo${unprocessed.length > 1 ? 's' : ''}`, 'success')
    } else {
      showToast(`Processed ${processed} of ${unprocessed.length} — ${failedCount} failed. Click Process All to retry.`, 'error')
    }
  }

  /** Render one photo, store it, and for the lead record its background removal. Throws on failure. */
  async function processPhoto(photo, isLead) {
    const needsBgRemoval = isLead && !photo.bg_removal_used

    let processedBlob
    if (needsBgRemoval) {
      const bgResult = await removeBackground(`${photo.url}?t=${Date.now()}`)
      processedBlob = await applyColourAndRatioBlob(bgResult.image_data, bgResult.content_type)
    } else {
      const { base64, contentType } = await urlToBase64(`/api/warehouse/photography/photos/${photo.photography_photo_id}/raw`)
      processedBlob = await applyColourAndRatioBlob(base64, contentType)
    }

    // Retries transient 502/503/504s internally (see photoUpload.js).
    await uploadProcessedPhoto(itemId, photo.photography_photo_id, processedBlob)

    // Record the lead's background removal only AFTER its processed image is
    // stored, so a failed upload retries WITH the cutout next time instead
    // of falling through to a no-cutout reprocess.
    if (needsBgRemoval) {
      setPhotos(prev => prev.map(p =>
        p.photography_photo_id === photo.photography_photo_id ? { ...p, bg_removal_used: 1 } : p
      ))
      try {
        await api.post(`/api/warehouse/photography/${itemId}/photos/${photo.photography_photo_id}/bg-removal-used`, {
          source: 'batch-process'
        })
      } catch (logErr) {
        console.error('Failed to log bg removal usage:', logErr)
      }
    }
  }

  // --- Complete ---

  async function handleComplete() {
    setCompleting(true)
    try {
      await api.post(`/api/warehouse/photography/${itemId}/complete`)
      onComplete()
    } catch (error) {
      console.error('Failed to complete photography:', error)
      showToast(`Failed to complete: ${error.message || 'unknown error'}`)
    } finally {
      setCompleting(false)
    }
  }

  const allProcessed = photos.length > 0 && photos.every(p => p.is_processed)
  const leadHasBgRemoval = photos.length > 0 && photos[0]?.bg_removal_used
  const canComplete = allProcessed && leadHasBgRemoval
  const processedCount = photos.filter(p => p.is_processed).length

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size={28} />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Back button + title. The heading is the item's make+model identity
          (product_name, e.g. "Yamaha HS8 - Studio Monitors") so the shooter can
          see what they're photographing at a glance; falls back to a generic
          label if the queue row carries no name. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Button variant="ghost" size="sm" onClick={onBack}>Back to Queue</Button>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: theme.colors.charcoal }}>
          {item.product_name || 'Photo Editor'}
        </h2>
        <UnitsNote quantity={item.quantity} />
      </div>

      {/* Item info bar */}
      <InfoBar item={item} />
      <QueuedReturn item={{ ...item, source_return_product_id: returnProductId }} onChanged={setReturnProductId} />

      {/* Upload zone */}
      <UploadZone
        isDragOver={isDragOverZone}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onDrop={handleDropOnZone}
        onDragOver={handleDragOverZone}
        onDragLeave={handleDragLeaveZone}
        onClickBrowse={() => fileInputRef.current?.click()}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />

      {/* QR Code Upload */}
      <Card>
        {qrUrl ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ display: 'inline-block', padding: 12, background: '#fff', borderRadius: 8 }}>
              <QRCodeSVG value={qrUrl} size={160} />
            </div>
            <div style={{ fontSize: 13, color: theme.colors.grey500, marginTop: 8 }}>
              Scan with your phone to upload photos directly
            </div>
            <div style={{ fontSize: 11, color: theme.colors.grey400, marginTop: 4 }}>
              Expires {new Date(qrSession.expiresAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <button
              onClick={createQrSession}
              style={{ marginTop: 8, background: 'none', border: 'none', color: theme.colors.imBlue, cursor: 'pointer', fontSize: 12 }}
            >
              Generate new code
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <button
              onClick={createQrSession}
              disabled={qrLoading}
              style={{
                padding: '8px 16px',
                background: theme.colors.charcoal,
                color: theme.colors.white,
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500
              }}
            >
              {qrLoading ? 'Generating...' : '📱 Upload from Phone (QR Code)'}
            </button>
          </div>
        )}
      </Card>

      {/* Photo grid */}
      {photos.length > 0 && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.charcoal }}>
              Photos ({photos.length})
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {photos.length > 1 && (
                <span style={{ fontSize: '12px', color: theme.colors.grey400 }}>
                  Drag to reorder
                </span>
              )}
              <span style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                {processedCount}/{photos.length} processed
              </span>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '12px'
          }}>
            {photos.map((photo, index) => (
              <PhotoThumbnail
                key={photo.photography_photo_id}
                photo={photo}
                index={index}
                isLead={index === 0}
                isDropTarget={dragOverIndex === index && draggingIndex !== index}
                isDragging={draggingIndex === index}
                isAnyDragging={draggingIndex !== null}
                locked={processing}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDropOnThumb(e, index)}
                onPreview={() => setPreviewPhoto({ ...photo, isLead: index === 0 })}
                onDelete={() => handleDeletePhoto(photo.photography_photo_id)}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Processing + completion controls */}
      {photos.length > 0 && (
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {processing && (
              <ProgressBar
                current={processProgress.current}
                total={processProgress.total}
                label="Processing photos"
              />
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                onClick={handleProcessAll}
                disabled={processing || photos.length === 0}
              >
                {processing ? 'Processing...' : 'Process All'}
              </Button>
              <Button
                variant="success"
                onClick={handleComplete}
                disabled={!canComplete || completing}
                title={allProcessed && !leadHasBgRemoval ? 'Lead image needs background removal' : undefined}
              >
                {completing ? 'Creating Draft...' : allProcessed && !leadHasBgRemoval ? 'Lead image needs BG removal' : 'Complete & Create Draft'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Photo edit modal */}
      <PhotoEditModal
        key={previewPhoto?.photography_photo_id}
        photo={previewPhoto}
        itemId={itemId}
        onClose={() => setPreviewPhoto(null)}
        onProcessed={loadPhotos}
        onBgRemovalUsed={(photoId) => {
          setPhotos(prev => prev.map(p =>
            p.photography_photo_id === photoId ? { ...p, bg_removal_used: 1 } : p
          ))
          if (previewPhoto?.photography_photo_id === photoId) {
            setPreviewPhoto(prev => prev ? { ...prev, bg_removal_used: 1 } : prev)
          }
        }}
      />

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          padding: '12px 24px', borderRadius: theme.radius.md,
          background: toast.type === 'error' ? theme.colors.crimson : theme.colors.greenOnions,
          color: theme.colors.white, fontSize: '0.9em', fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 9999,
          maxWidth: '500px', textAlign: 'left', whiteSpace: 'pre-line'
        }}>
          {toast.message}
        </div>
      )}
    </div>
  )
}


// --- PhotoEditor sub-components ---

function InfoBar({ item }) {
  return (
    <Card>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '24px',
        fontSize: '14px', color: theme.colors.charcoal
      }}>
        <InfoField label="Brand" value={item.brand || '-'} />
        <InfoField label="Product Type" value={item.product_type || '-'} />
        <InfoField label="Trade ID" value={<PhotographyDealLink item={item} />} />
        <InfoField label="Condition" value={photographyConditionLabel(item.overall_condition) || '-'} />
        <InfoField label="Included" value={photographyAccessoriesLabel(item.accessories)} />
        <InfoField label="Source" value={<SourceBadge source={item.source} />} />
        <InfoField label="Sell Price" value={formatCurrency(item.sell_price)} />
        {/* Ownership chip in the editor too — the shooter is one click from
            creating the draft, and this one is a customer's item. */}
        {item.is_listing_service === 1 && (
          <InfoField label="Ownership" value={<Badge color="purple">Consignment</Badge>} />
        )}
      </div>
    </Card>
  )
}

// The trade an item came from, opening its deal in a new tab so the shoot in
// progress is kept. A row with no deal id shows its reference, or a dash.
function PhotographyDealLink({ item }) {
  if (!item.source_deal_id) return item.source_reference || '-'
  return <DealLink dealId={item.source_deal_id} reference={item.source_reference} newTab />
}

function InfoField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '12px', color: theme.colors.grey500, fontWeight: 600, marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  )
}

function UploadZone({ isDragOver, uploading, uploadProgress, onDrop, onDragOver, onDragLeave, onClickBrowse }) {
  const pct = uploadProgress.pct || 0

  return (
    <Card>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={{
          border: `2px dashed ${isDragOver ? theme.colors.sunburst : theme.colors.grey300}`,
          borderRadius: theme.radius.md,
          padding: '40px',
          textAlign: 'center',
          background: isDragOver ? theme.colors.amberLight : theme.colors.grey100,
          transition: 'all 0.2s ease',
          cursor: uploading ? 'default' : 'pointer'
        }}
        onClick={uploading ? undefined : onClickBrowse}
      >
        {uploading ? (
          <div style={{ maxWidth: '300px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
              <span style={{ color: theme.colors.charcoal, fontWeight: 600 }}>
                {uploadProgress.total > 1
                  ? `Uploading ${uploadProgress.current + 1} of ${uploadProgress.total}...`
                  : 'Uploading...'}
              </span>
              <span style={{ color: theme.colors.grey500 }}>{pct}%</span>
            </div>
            <div style={{
              height: '8px', borderRadius: '4px',
              background: theme.colors.grey200, overflow: 'hidden'
            }}>
              <div style={{
                height: '100%', borderRadius: '4px',
                background: theme.colors.sunburst,
                width: `${pct}%`,
                transition: 'width 0.2s ease'
              }} />
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.charcoal, marginBottom: '4px' }}>
              Drop photos here or click to browse
            </div>
            <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
              Accepts JPG, PNG, WebP
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

const PhotoThumbnail = memo(function PhotoThumbnail({
  photo, index, isLead, isDropTarget, isDragging, isAnyDragging, locked,
  onDragStart, onDragEnd, onDragOver, onDrop,
  onPreview, onDelete
}) {
  const imageUrl = photo.processedUrl || photo.url
  // A failed thumbnail (resizing off, quota used up) falls back to the full image.
  const [thumbFailed, setThumbFailed] = useState(false)
  useEffect(() => setThumbFailed(false), [imageUrl])
  const tileUrl = thumbFailed ? imageUrl : photoThumbnailUrl(imageUrl)

  // Locked while Process All runs: no reorder, no edit modal, no delete.
  return (
    <div
      draggable={!locked}
      onDragStart={locked ? undefined : onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={locked ? undefined : onDragOver}
      onDrop={locked ? undefined : onDrop}
      style={{
        position: 'relative',
        borderRadius: theme.radius.md,
        overflow: 'hidden',
        border: `1px solid ${theme.colors.grey200}`,
        background: theme.colors.grey100,
        cursor: locked ? 'default' : 'grab',
        aspectRatio: '4/5',
        opacity: isDragging ? 0.3 : 1,
        boxShadow: isDropTarget
          ? `0 0 0 3px ${theme.colors.sunburst}, ${theme.shadows.md}`
          : isDragging
            ? `0 0 0 3px ${theme.colors.imBlue}`
            : 'none',
        transform: isDropTarget ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 30ms ease, opacity 30ms ease, box-shadow 30ms ease'
      }}
    >
      {/* Image */}
      <img
        src={tileUrl}
        alt={photo.filename}
        draggable={false}
        onError={tileUrl !== imageUrl ? () => setThumbFailed(true) : undefined}
        onClick={locked ? undefined : onPreview}
        style={{
          width: '100%', height: '100%',
          objectFit: 'cover', display: 'block',
          cursor: locked ? 'default' : 'pointer',
          pointerEvents: isAnyDragging ? 'none' : 'auto'
        }}
      />

      {/* Position badge */}
      <span style={{
        position: 'absolute', top: '6px', left: '6px',
        padding: '2px 8px', borderRadius: '12px',
        fontSize: '11px', fontWeight: 700,
        background: isLead ? theme.colors.sunburst : theme.colors.white,
        color: theme.colors.charcoal,
        boxShadow: theme.shadows.sm,
        pointerEvents: 'none'
      }}>
        {isLead ? 'Lead' : `#${index + 1}`}
      </span>

      {/* Processed checkmark */}
      {photo.is_processed && (
        <span style={{
          position: 'absolute', top: '6px', right: '6px',
          width: '22px', height: '22px', borderRadius: '50%',
          background: theme.colors.greenOnions, color: theme.colors.white,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: 700,
          pointerEvents: 'none'
        }}>
          &#10003;
        </span>
      )}

      {/* Drag grip indicator */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '8px 0 5px',
        display: 'flex', justifyContent: 'center', gap: '3px',
        background: 'linear-gradient(transparent, rgba(0,0,0,0.45))',
        pointerEvents: 'none'
      }}>
        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)' }} />
        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)' }} />
        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)' }} />
      </div>

      {/* Delete button */}
      {!locked && <button
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        style={{
          position: 'absolute', bottom: '6px', right: '6px',
          width: '24px', height: '24px', borderRadius: '50%',
          background: theme.colors.crimson, color: theme.colors.white,
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', fontWeight: 700, zIndex: 1
        }}
      >
        x
      </button>}
    </div>
  )
})


// =============================================
// Root Photography component
// =============================================

function Photography() {
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [activeItem, setActiveItem] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkDone = useRef(false)
  const [removing, setRemoving] = useState(null)
  const [toast, setToast] = useState(null)

  useEffect(() => {
    loadItems()
  }, [])

  // Honor a ?item=<photography_queue_id> deep link once the queue has loaded —
  // this is how "Finish in Photography" on the Repairs page jumps straight into
  // the editor. Fires once; the param is cleared so Back returns to the queue.
  useEffect(() => {
    if (deepLinkDone.current || loading) return
    deepLinkDone.current = true
    const wanted = searchParams.get('item')
    if (!wanted) return
    const match = items.find(i => String(i.photography_queue_id) === String(wanted))
    if (match) {
      setActiveItem(match)
      searchParams.delete('item')
      setSearchParams(searchParams, { replace: true })
    }
  }, [loading, items, searchParams, setSearchParams])

  async function loadItems() {
    setLoading(true)
    try {
      const response = await api.get('/api/warehouse/photography')
      setItems(response.data || response.items || [])
      setLoadFailed(false)
    } catch (error) {
      // Say so: an empty list here reads as "every item has gone" (Trac #1354).
      console.error('Failed to load photography items:', error)
      setLoadFailed(true)
    } finally {
      setLoading(false)
    }
  }

  function handleStartItem(item) {
    setActiveItem(item)
  }

  function handleBack() {
    setActiveItem(null)
    loadItems()
  }

  function handleRemoved(item) {
    setItems(current => current.filter(i => i.photography_queue_id !== item.photography_queue_id))
    setRemoving(null)
    setToast(`Removed ${item.product_name} from the queue`)
    setTimeout(() => setToast(null), TOAST_MS)
  }

  function handleComplete() {
    setActiveItem(null)
    loadItems()
  }

  const isAdmin = Boolean(user?.is_admin)

  if (activeItem) {
    // The notes sit in the space left of the 1200px editor, which stays centred.
    return (
      <QuoteNotesLayout dealId={activeItem.source_deal_id} mainWidth={1200} style={{ padding: '24px', fontFamily: theme.fonts.sans }}>
        <PhotoEditor item={activeItem} onBack={handleBack} onComplete={handleComplete} />
      </QuoteNotesLayout>
    )
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: theme.fonts.sans }}>
      <QueueView
        items={items}
        loading={loading}
        loadFailed={loadFailed}
        onStartItem={handleStartItem}
        onRemoveItem={setRemoving}
        onRefresh={loadItems}
        isAdmin={isAdmin}
      />
      {removing && (
        <RemoveFromQueueDialog item={removing} onRemoved={handleRemoved} onClose={() => setRemoving(null)} />
      )}
      {toast && (
        <div role="status" style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          padding: '12px 24px', borderRadius: theme.radius.md,
          background: theme.colors.greenOnions, color: theme.colors.white,
          fontSize: '0.9em', fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 9999
        }}>
          {toast}
        </div>
      )}
    </div>
  )
}

export default Photography
